import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import { useLocalStorageState } from "../../utils/react/useLocalStorageState";

/*
 * TODO: Per-pane state is wired straight to localStorage as an interim. The
 * agreed plan is to expose user preferences through `artis.preferences`, a
 * Web Storage-compatible API (https://developer.mozilla.org/en-US/docs/Web/API/Storage)
 * backed by localStorage today and swappable for a server-backed implementation
 * later without touching consumers. Each feature owns its own key
 * (e.g. `multi-pane:pane-<id>:pageId`), serializes its own value, and handles
 * its own validation, versioning, and migration locally. When that lands,
 * replace the useLocalStorageState calls below with a reactive `usePreference`
 * hook over `artis.preferences`. See multi-pane plan in local-docs.
 */

export const DEFAULT_PANE_ID = "default";

const PaneIdContext = createContext<string>(DEFAULT_PANE_ID);

type PaneActivePage = readonly [string | undefined, Dispatch<SetStateAction<string | undefined>>];
const PaneActivePageContext = createContext<PaneActivePage | null>(null);

export type PaneActions = {
  splitCurrent: (orientation: "horizontal" | "vertical") => void;
  closeCurrent: () => void;
  canClose: boolean;
  hasScratchpad: boolean;
  toggleScratchpad: () => void;
};
const PaneActionsContext = createContext<PaneActions | null>(null);

export const usePaneId = () => useContext(PaneIdContext);
export const usePaneActivePageId = () => useContext(PaneActivePageContext);
export const usePaneActions = () => useContext(PaneActionsContext);

export type PaneContextValue = {
  paneId: string;
  activePage: PaneActivePage | null;
  actions: PaneActions | null;
};

/**
 * Snapshot the surrounding pane, for replaying it somewhere else in the tree.
 * Outside a pane this yields the same defaults the contexts carry on their own,
 * so replaying it is a no-op.
 */
export const usePaneContext = (): PaneContextValue => {
  const paneId = useContext(PaneIdContext);
  const activePage = useContext(PaneActivePageContext);
  const actions = useContext(PaneActionsContext);
  return useMemo(() => ({ paneId, activePage, actions }), [paneId, activePage, actions]);
};

/**
 * Supply pane context from an existing value. Content rendered through a portal
 * or a registry (modals) sits outside the pane that owns it, and uses this to
 * inherit the opening pane rather than whatever surrounds the render site.
 */
export function PaneContextProvider({ value, children }: { value: PaneContextValue; children: ReactNode }) {
  return (
    <PaneIdContext.Provider value={value.paneId}>
      <PaneActivePageContext.Provider value={value.activePage}>
        <PaneActionsContext.Provider value={value.actions}>{children}</PaneActionsContext.Provider>
      </PaneActivePageContext.Provider>
    </PaneIdContext.Provider>
  );
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ TEMPORARY: single-grid shim                                              │
 * │ TODO! REMOVE WHEN shared chrome stops resolving pane state for itself.   │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * WHY IT EXISTS
 * Shell chrome (the action menu in Sidebar, CollapsibleSettings) renders as a
 * sibling of the <Outlet> that hosts the panes, so it is outside every
 * PaneProvider and sees no pane. Two pieces of pane-scoped state break as a
 * result, both silently:
 *
 *   - the page id. There is no `/app/market/$id` route param to fall back to
 *     either, so `useOptionalActivePageId` resolved to undefined and chrome
 *     actions operated on an empty page: `usePageProducts(undefined)` matches
 *     no rows, so saving did nothing and reported nothing.
 *   - the grid api. `useGrid` reads `gridApiAtomFamily(usePaneId())`, which
 *     outside a pane is the DEFAULT slot, while the grid registered itself
 *     under its real pane id. Chrome handlers that start with
 *     `if (!gridApi) return` became no-ops: "To fit content" in the action
 *     menu, and everything else on the useGrid list below.
 *
 * WHAT IT ASSUMES
 * That only one grid is on screen at a time, which holds while the pane system
 * is what it is today. Under that assumption the pane that last published is
 * that one grid, so chrome can borrow its page id and its api. Split into two
 * grids and the values become last-writer-wins: that is the assumption
 * expiring, not a bug to patch. If you find yourself adding tie-breaking rules
 * here, stop, the shim has outlived its premise and the real fix below is due.
 *
 * WHAT REPLACES IT
 * Shared chrome should not resolve pane state at all. It should talk to a pane
 * over the artis api, which keeps the pane owning its own context and avoids
 * inventing an "active pane" concept that the pane model deliberately does not
 * have. The global action menu is expected to disappear with multi-pane, in
 * which case most of the call sites below go with it rather than being ported.
 * Note this is NOT the same problem as modals: those are owned by the pane that
 * opens them and are handled properly by capturing pane context at open time
 * (see usePaneContext / PaneContextProvider above).
 *
 * HOW TO REMOVE IT
 *   1. Delete this block and both stores below, down to `useSoleGridPaneId`.
 *   2. Drop the `soleGrid` fallback from `useOptionalActivePageId`
 *      (data/pages/page.ts) and the publishing effect in PaneProvider below.
 *   3. Drop the fallback in `useGrid` (market-grid/stores/index.ts) so it reads
 *      `usePaneId()` again, and the publishing effect in
 *      artis-modules/MarketGridModule.
 *   4. Delete pane/sole-grid-page-id.test.tsx, which pins this behaviour.
 *   5. Point the chrome call sites at the artis api, or delete them with the
 *      global action menu: components/ActionsMenu/{ActionsMenu,StatusModal,
 *      SetAllColumnWidthsModal}.tsx, useSetAllFuturesFieldsAction.tsx,
 *      useReconfirmDataLogic.ts, grid-settings/Settings.tsx,
 *      TracePrecedents/hooks, grid-charts/hooks.ts, PageAggregationResults.tsx.
 * TypeScript will find anything this list has missed once step 1 is done.
 */
const createSoleGridStore = () => {
  let current: string | undefined;
  const subscribers = new Set<() => void>();
  // Stable identities: useSyncExternalStore resubscribes whenever `subscribe` changes.
  const subscribe = (notify: () => void) => {
    subscribers.add(notify);
    return () => {
      subscribers.delete(notify);
    };
  };
  const getSnapshot = () => current;
  return {
    publish: (value: string) => {
      if (value === current) return;
      current = value;
      for (const notify of subscribers) notify();
    },
    use: () => useSyncExternalStore(subscribe, getSnapshot),
  };
};

const soleGridPage = createSoleGridStore();
const soleGridPane = createSoleGridStore();

const publishSoleGridPageId = soleGridPage.publish;
export const useSoleGridPageId = soleGridPage.use;

/** The pane hosting the market grid, for chrome that renders outside every pane. */
export const publishSoleGridPaneId = soleGridPane.publish;
export const useSoleGridPaneId = soleGridPane.use;

export function PaneProvider({
  paneId,
  actions,
  children,
}: {
  paneId: string;
  actions?: PaneActions;
  children: ReactNode;
}) {
  const [pageId, setPageId] = useLocalStorageState<string | undefined>(`multi-pane:pane-${paneId}:pageId`, undefined);

  // TODO! REMOVE WITH the single-grid page id shim above.
  useEffect(() => {
    if (pageId) publishSoleGridPageId(pageId);
  }, [pageId]);

  const value = useMemo<PaneContextValue>(
    () => ({ paneId, activePage: [pageId, setPageId], actions: actions ?? null }),
    [paneId, pageId, setPageId, actions],
  );
  return <PaneContextProvider value={value}>{children}</PaneContextProvider>;
}

export const marketGridId = (paneId: string) => (paneId === DEFAULT_PANE_ID ? "marketGrid" : `marketGrid-${paneId}`);
